import { Popup, SelectBox } from 'devextreme-react';
import { DropDownOptions } from 'devextreme-react/cjs/select-box';
import HtmlEditor, {
  Toolbar,
  MediaResizing,
  ImageUpload,
  Item,
} from 'devextreme-react/html-editor';
import { Select } from 'flowbite-react';
import { useCallback, useMemo, useRef, useState } from 'react';

const sizeValues = ['8pt', '10pt', '12pt', '14pt', '18pt', '24pt', '36pt'];
const fontValues = [
  'Arial',
  'Courier New',
  'Georgia',
  'Impact',
  'Lucida Console',
  'Tahoma',
  'Times New Roman',
  'Verdana',
];
const headerValues = [false, 1, 2, 3, 4, 5];
const fontSizeOptions = {
  inputAttr: {
    'aria-label': 'Font size',
  },
};
const fontFamilyOptions = {
  inputAttr: {
    'aria-label': 'Font family',
  },
};
const headerOptions = {
  inputAttr: {
    'aria-label': 'Font family',
  },
};

export default function Editor({
  id,
  name,
  className,
  value,
  changedValue,
  placeholders,
}: {
  id: string;
  name: string;
  className: string | undefined;
  value: string;
  changedValue: (value: string) => void;
  placeholders?: string[] | undefined;
}) {
  const htmlEditor = useRef<HtmlEditor>(null);
  const [placeholderValue, setplaceholderValue] = useState('');
  const [popupVisible, setPopupVisible] = useState(false);

  const customButtonClick = useCallback(() => {
    setPopupVisible(true);
  }, [setPopupVisible]);

  const popupHiding = useCallback(() => {
    setPopupVisible(false);
  }, [setPopupVisible]);

  const getToolbarButtonOptions = useMemo(
    () => ({
      text: 'Show markup',
      stylingMode: 'text',
      onClick: customButtonClick,
    }),
    [customButtonClick]
  );

  const placeholderOptions = useMemo(() => {
    const list = placeholders?.map((obj) => ({
      text: obj,
      value: obj,
    }));
    return list;
  }, []);

  const selectBoxOptions = useMemo(
    () => ({
      width: 200,
      items: placeholderOptions,
      displayExpr: 'text',
      valueExpr: 'value',
      value: placeholderValue,
      onValueChanged: (args) => {
        setplaceholderValue(args.value);
      },
    }),
    [placeholderValue]
  );
  const addPlaceholderButtonOptions = useMemo(
    () => ({
      text: 'Add Placeholder',
      onClick: () => {
        const newPosition = htmlEditor?.current?.instance.getSelection(true);
        htmlEditor?.current!.instance.insertText(
          newPosition.index,
          placeholderValue ?? '',
          {}
        );
      },
    }),
    [placeholderValue]
  );

  return (
    <div className='widget-container w-full'>
      <HtmlEditor
        ref={htmlEditor}
        height='500px'
        defaultValue={''}
        className={className}
        name={name}
        value={value}
        onValueChange={changedValue}
        id={id}
      >
        <MediaResizing enabled={true} />
        <ImageUpload tabs={['file', 'url']} fileUploadMode='base64' />
        <Toolbar multiline>
          <Item name='undo' />
          <Item name='redo' />
          <Item name='separator' />
          <Item
            name='size'
            acceptedValues={sizeValues}
            options={fontSizeOptions}
          />
          <Item
            name='font'
            acceptedValues={fontValues}
            options={fontFamilyOptions}
          />
          <Item name='separator' />
          <Item name='bold' />
          <Item name='italic' />
          <Item name='strike' />
          <Item name='underline' />
          <Item name='separator' />
          <Item name='alignLeft' />
          <Item name='alignCenter' />
          <Item name='alignRight' />
          <Item name='alignJustify' />
          <Item name='separator' />
          <Item name='orderedList' />
          <Item name='bulletList' />
          <Item name='separator' />
          <Item
            name='header'
            acceptedValues={headerValues}
            options={headerOptions}
          />
          <Item name='clear' />
          <Item
            widget='dxButton'
            options={getToolbarButtonOptions}
            visible={false}
          />
          {placeholders && (
            <Item widget='dxSelectBox' options={selectBoxOptions}></Item>
          )}
          <Item
            locateInMenu='always'
            widget='dxButton'
            options={addPlaceholderButtonOptions}
          />
        </Toolbar>
      </HtmlEditor>
      <Popup
        showTitle={true}
        title='Markup'
        visible={popupVisible}
        onHiding={popupHiding}
        showCloseButton={true}
      >
        <div>{value}</div>
      </Popup>
    </div>
  );
}
